// Phase 7 D3 — Track C T-C5 + Track F T-F2. Gmail status hook.
//
// `GET /api/modules/payments/email/status` returns an array of EmailConnection
// rows (Phase 3 shape). The SPA treats the user as "connected" when at least
// one row has status='connected'. Disconnect targets a specific
// accountEmailHash; the dialog identifies the active connection.
//
// Phase 7.5 T-B3 — discriminated-union state. The subsystem is unmounted when
// the operator hasn't provisioned GOOGLE_OAUTH_CLIENT_ID, in which case the
// route returns 404. We surface that as `unconfigured` (informational) rather
// than `error` (destructive) so first-time operators don't see a red toast.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import { ApiError } from '@dailydeck/shared/api';
import type { EmailConnection, EmailConnectionsResponse } from '@dailydeck/shared/types';
import { apiClient } from '../app/apiClient.js';

export const GMAIL_STATUS_QUERY_KEY = ['payments', 'email', 'status'] as const;

export type GmailStatusState =
  | { kind: 'loading' }
  | { kind: 'unconfigured' }
  | { kind: 'connected'; rows: readonly EmailConnection[] }
  | { kind: 'disconnected' }
  | { kind: 'error' };

export function useGmailStatus(): GmailStatusState {
  const query = useQuery<EmailConnectionsResponse, Error>({
    queryKey: GMAIL_STATUS_QUERY_KEY,
    queryFn: () => apiClient.payments.emailStatus(),
    // The unmounted-route 404 is a stable signal, not a transient failure;
    // retrying cannot succeed without a backend restart.
    retry: (_count, err) => !(err instanceof ApiError && err.status === 404),
  });

  if (query.isPending) return { kind: 'loading' };
  if (query.isError) {
    // WHY: Express's default not-found handler returns HTML so decodeError
    // produces ApiError(code: 'unknown', status: 404); a JSON 404 from the
    // route would carry code: 'not_found'. Match on status to catch both.
    if (query.error instanceof ApiError && query.error.status === 404) {
      return { kind: 'unconfigured' };
    }
    return { kind: 'error' };
  }
  const rows = query.data;
  const hasConnected = rows.some((r) => r.status === 'connected');
  if (hasConnected) return { kind: 'connected', rows };
  return { kind: 'disconnected' };
}

export function useGmailDisconnect(): UseMutationResult<
  void,
  Error,
  { accountEmailHash: string }
> {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { accountEmailHash: string }>({
    mutationFn: (args) => apiClient.payments.emailDisconnect(args),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: GMAIL_STATUS_QUERY_KEY });
    },
  });
}
